import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../hooks";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
  useMediaQuery,
  DialogActions,
  Button,
  Box,
  Drawer,
  IconButton,
  Accordion,
  AccordionSummary,
  Divider,
  Fab,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ForumIcon from "@mui/icons-material/Forum";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  forwardRef,
} from "react";
import StageLinkLiveStream from "./StageLinkLiveStream";
import { zegoInit } from "./../../../utils/zegoInit";
import LiveChat from "../LiveChat";
import GuestManagement from "../Talent/GuestManagement";

/* ── Theme tokens (Netflix-ish) ─────────────────────────────────────── */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  red: "#E50914",
  redHover: "#b20710",
};
const RADIUS = 2;

export function Panel({
  title,
  icon,
  expanded,
  onToggle,
  onClose,            // ← new: collapse via “X”
  children,
  panelRef,
}: {
  title: string;
  icon?: React.ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
  onClose?: () => void;
  children: React.ReactNode;
  panelRef?: React.RefObject<HTMLDivElement>;
}) {
  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={onToggle}
      ref={panelRef as any}
      TransitionProps={{ unmountOnExit: false }}
      sx={{
        bgcolor: COLORS.panel,
        color: COLORS.text,
        border: "1px solid",
        borderColor: expanded ? COLORS.red : COLORS.border,
        borderRadius: RADIUS,
        "&::before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon htmlColor={COLORS.textMuted} />}
        sx={{
          minHeight: 56,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            gap: 1,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
          {icon}
          <Typography sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}>
            {title}
          </Typography>

          {/* Right actions (close) */}
          {onClose && (
            <Box sx={{ ml: "auto" }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation(); // don't toggle accordion
                  onClose();
                }}
                sx={{ color: COLORS.textMuted }}
                aria-label={`close ${title}`}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <Divider sx={{ borderColor: COLORS.border }} />
      <Box sx={{ p: 2 }}>{children}</Box>
    </Accordion>
  );
}

export const Surface = ({ children, sx }: { children: React.ReactNode; sx?: any }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: RADIUS,
      bgcolor: COLORS.panel,
      border: "1px solid",
      borderColor: COLORS.border,
      color: COLORS.text,
      ...sx,
    }}
  >
    {children}
  </Paper>
);
/* ── MOBILE: Panels below video (no drawer) ─────────────────────────── */
export function ToolsPaneMobileInline({
  joinUrl,
  bookingId,
  username,
  focusPanel,            // 'qr' | 'chat' | 'guest' | null
  onFocusedHandled,
}: {
  joinUrl: string;
  bookingId: string;
  username?: string;
  focusPanel: 'qr' | 'chat' | 'guest' | null;
  onFocusedHandled: () => void;
}) {
  const [openQR, setOpenQR] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [openGuest, setOpenGuest] = useState(false);
  const [copied, setCopied] = useState(false);

  const qrRef = useRef<HTMLDivElement | null>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const guestRef = useRef<HTMLDivElement | null>(null);

  // Honor external “focus this panel” (e.g., QR button from video toolbar)
  useEffect(() => {
    const map: Record<string, { setter: (v: boolean) => void; ref: any }> = {
      qr: { setter: setOpenQR, ref: qrRef },
      chat: { setter: setOpenChat, ref: chatRef },
      guest: { setter: setOpenGuest, ref: guestRef },
    };
    if (focusPanel && map[focusPanel]) {
      map[focusPanel].setter(true);
      requestAnimationFrame(() => {
        map[focusPanel].ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      onFocusedHandled();
    }
  }, [focusPanel, onFocusedHandled]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  }, [joinUrl]);

  return (
    <Box
      sx={{
        px: 2,
        pb: 'env(safe-area-inset-bottom)',
        display: 'grid',
        gap: 2,
      }}
    >
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => { setOpenQR(false); setOpenChat(false); setOpenGuest(false); }}
          sx={{
            borderRadius: RADIUS,
            textTransform: 'none',
            color: COLORS.text,
            borderColor: COLORS.border,
            '&:hover': { borderColor: COLORS.textMuted },
          }}
        >
          Hide all
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => { setOpenQR(true); setOpenChat(true); setOpenGuest(true); }}
          sx={{
            borderRadius: RADIUS,
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: COLORS.red,
            '&:hover': { bgcolor: COLORS.redHover },
          }}
        >
          Show all
        </Button>
      </Box>

      {/* QR Join */}
      <Panel
        title="QR Join"
        icon={<QrCode2Icon fontSize="small" />}
        expanded={openQR}
        onToggle={() => setOpenQR(v => !v)}
        onClose={() => setOpenQR(false)}
        panelRef={qrRef}
      >
        <Surface sx={{ textAlign: 'center' }}>
          <QRCodeSVG value={joinUrl} size={180} level="H" includeMargin />
          <Typography
            variant="body2"
            sx={{ mt: 2, color: COLORS.textMuted, wordBreak: 'break-all' }}
          >
            {joinUrl}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={copyLink}
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              sx={{
                borderRadius: RADIUS,
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: COLORS.red,
                '&:hover': { bgcolor: COLORS.redHover },
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </Box>
        </Surface>
      </Panel>

      {/* Live Chat */}
      <Panel
        title="Live Chat"
        icon={<ForumIcon fontSize="small" />}
        expanded={openChat}
        onToggle={() => setOpenChat(v => !v)}
        onClose={() => setOpenChat(false)}
        panelRef={chatRef}
      >
        <Surface
          sx={{
            p: 0,
            // Cap the height so it doesn’t consume the whole screen; still scrollable
            maxHeight: 'min(62vh, 520px)',
            overflow: 'hidden',
          }}
        >
          <LiveChat
            bookingId={bookingId}
            username={username || 'talent'}
            height={'56vh'} // your component already accepts height
          />
        </Surface>
      </Panel>

      {/* Guest Management (optional on mobile; keep collapsible) */}
      <Panel
        title="Guest Management"
        icon={<ForumIcon fontSize="small" />}
        expanded={openGuest}
        onToggle={() => setOpenGuest(v => !v)}
        onClose={() => setOpenGuest(false)}
        panelRef={guestRef}
      >
        <Surface sx={{ p: 0 }}>
          <GuestManagement bookingId={bookingId} />
        </Surface>
      </Panel>
    </Box>
  );
}
